import React from "react";
import propTypes from "prop-types";
import { useFormContext } from "./Form";
import useField, { fieldPropTypes, getId } from "./field";

const checkable = ["checkbox", "radio"];

/**
 * @typedef {Object} InputProps
 * @property {string | number} value The value for this field. For most field
 *   types, this will result in a read-only field, but for "hidden", "checkbox",
 *   or "radio" fields, it provides the actual value for the field.
 * @property {string} [type=text] The field type. This should be a valid value
 *   for the `type` property on an `input` element, as defined in the HTML spec.
 */

/**
 * Creates an HTML `<input />` element.
 *
 * @param {import("./field").StandardFieldProps & InputProps} props
 *
 * @return [Element[]] The new input field, followed by any errors.
 */
const Input = ({
    checked,
    type = "text",
    ...props
}) => {
    const isCheckable = checkable.includes(type);
    const optionValue = props.value;

    const context = useFormContext();
    const {
        name,
        key,
        errors,
        fieldProps,
        ...fieldData
    } = useField(props, context, ["id"]);
    let { id, value } = fieldData;

    if (isCheckable) {
        id = getId(props.name, context, props.value);
        value = context.form.getValue(name, "");
        checked = checked ||  (value && optionValue == value);
        fieldProps.value = optionValue;
    }

    return [
        <input
            id={id}
            key={key}
            {...isCheckable && {checked}}
            type={type}
            {...fieldProps}
        />,
        ...errors.elements,
    ];
};

Input.propTypes = {
    ...fieldPropTypes,
    type: propTypes.string,
    checked: propTypes.bool,
};

export default Input;
